import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  FooterConfig,
  HeaderConfig,
  PdfLayout,
  PdfLayoutGroup,
  PdfLayoutSection,
} from '../../../utils/api/pdfLayout';
import HeaderFooterBlock from './HeaderFooterBlock';
import PlacementBlock from './PlacementBlock';
import SectionBlock from './SectionBlock';

interface Props {
  templateName: string;
  layout: PdfLayout;
  sections: Record<number, PdfLayoutSection>;
  groups: PdfLayoutGroup[];
  logoGuid: string | null;
  onPatch: (fields: Partial<PdfLayout>) => void;
  onPatchSection: (groupId: number, fields: Partial<PdfLayoutSection>) => void;
}

export type LayoutItem =
  | { key: string; type: 'section'; groupId: number }
  | { key: 'cert'; type: 'cert' }
  | { key: 'findings'; type: 'findings' };

export const defaultSection = (groupId: number): PdfLayoutSection => ({
  question_group_id: groupId,
  question_appearance: 'border',
  style_config: {
    width: '100%',
    name_style: { fontStyle: 'normal', fontSize: '13px' },
    description_style: { fontStyle: 'normal', fontSize: '11px' },
    grid_style: {
      widthPerColumn: {},
      tableHeaderStyle: {
        fontStyle: 'normal',
        fontSize: '11px',
        fontWeight: 700,
      },
    },
    question_style: { fontStyle: 'normal', fontSize: '10px', fontWeight: 700 },
  },
  page_break_after: false,
});

export const buildItems = (
  groups: PdfLayoutGroup[],
  layout: PdfLayout,
): LayoutItem[] => {
  const items: LayoutItem[] = [];

  const certCustom = layout.certifications_config.placement === 'custom';
  const findingsCustom = layout.findings_config.placement === 'custom';

  const certAnchor = layout.certifications_config.after_group_id;
  const findingsAnchor = layout.findings_config.after_group_id;

  for (const group of groups) {
    items.push({
      key: `section-${group.id}`,
      type: 'section',
      groupId: group.id,
    });

    if (certCustom && certAnchor === group.id)
      items.push({ key: 'cert', type: 'cert' });

    if (findingsCustom && findingsAnchor === group.id)
      items.push({ key: 'findings', type: 'findings' });
  }

  if (certCustom && certAnchor == null)
    items.push({ key: 'cert', type: 'cert' });

  if (findingsCustom && findingsAnchor == null)
    items.push({ key: 'findings', type: 'findings' });

  if (!certCustom) items.push({ key: 'cert', type: 'cert' });

  if (!findingsCustom) items.push({ key: 'findings', type: 'findings' });

  return items;
};

// A sheet is the page box the backend prints: US Letter at 96dpi.
const SHEET: Record<PdfLayout['orientation'], [number, number]> = {
  portrait: [816, 1056],
  landscape: [1056, 816],
};

// [top, right, bottom, left] — mirrors the backend's PDF_MARGINS.
const MARGIN_PAD: Record<
  PdfLayout['margins'],
  [number, number, number, number]
> = {
  narrow: [30, 24, 34, 24],
  normal: [44, 44, 48, 44],
  wide: [60, 72, 64, 72],
};

const repeats = (config?: HeaderConfig | FooterConfig | null) =>
  !!config?.enabled && (config.repeat ?? 'all') === 'all';

const widthOf = (
  item: LayoutItem,
  sections: Record<number, PdfLayoutSection>,
) =>
  (item.type === 'section' && sections[item.groupId]?.style_config?.width) ||
  '100%';

const DocumentPreview: React.FC<Props> = ({
  templateName,
  layout,
  sections,
  groups,
  logoGuid,
  onPatch,
  onPatchSection,
}) => {
  const [sheetHeights, setSheetHeights] = useState<number[]>([]);
  const sheetsRef = useRef<HTMLDivElement>(null);

  const sortedGroups = useMemo(
    () =>
      groups
        .filter((group) => group.name !== 'Condition Group')
        .sort((a, b) => a.order - b.order),
    [groups],
  );

  const groupMap = useMemo(
    () => Object.fromEntries(sortedGroups.map((group) => [group.id, group])),
    [sortedGroups],
  );

  const items = useMemo(
    () => buildItems(sortedGroups, layout),
    [sortedGroups, layout],
  );

  const [sheetWidth, sheetHeight] = SHEET[layout.orientation] ?? SHEET.portrait;
  const [padTop, padRight, padBottom, padLeft] =
    MARGIN_PAD[layout.margins] ?? MARGIN_PAD.normal;

  const header = layout.header_config;
  const footer = layout.footer_config;

  // The section above each item owns the page-break toggle shown before it.
  const breakOwners = useMemo(() => {
    const owners: Record<string, number> = {};
    let previous: number | null = null;

    items.forEach((item) => {
      if (previous !== null) owners[item.key] = previous;
      if (item.type === 'section') previous = item.groupId;
    });

    return owners;
  }, [items]);

  // Items flow down a sheet, ending with `page_break_after` or a guide line.
  const sheets = useMemo(() => {
    const result: LayoutItem[][] = [[]];

    items.forEach((item) => {
      result[result.length - 1].push(item);

      if (item.type === 'section' && sections[item.groupId]?.page_break_after)
        result.push([]);
    });

    if (result.length > 1 && !result[result.length - 1].length) result.pop();

    return result;
  }, [items, sections]);

  // A sheet is as many printed pages as it has page boxes' worth of height.
  useLayoutEffect(() => {
    const next: number[] = [];

    sheetsRef.current
      ?.querySelectorAll<HTMLElement>('[data-ple-sheet]')
      .forEach((el) => next.push(el.offsetHeight));

    setSheetHeights((prev) =>
      prev.length === next.length && prev.every((h, i) => h === next[i])
        ? prev
        : next,
    );
    // Re-measure heights only when content or page box changes
  }, [sheets, sections, layout]);

  // first item of every sheet after the first: its break toggle lives in the gap
  const sheetStarts = useMemo(
    () => new Set(sheets.slice(1).map((sheet) => sheet[0]?.key)),
    [sheets],
  );

  const pageCounts = sheets.map((_, index) =>
    Math.max(1, Math.ceil((sheetHeights[index] ?? sheetHeight) / sheetHeight)),
  );
  const totalPages = pageCounts.reduce((sum, count) => sum + count, 0);

  const placementBlock = (kind: 'cert' | 'findings') => {
    const field = kind === 'cert' ? 'certifications_config' : 'findings_config';
    const config = layout[field];

    return (
      <PlacementBlock
        kind={kind === 'cert' ? 'certification' : 'findings'}
        placement={config.placement}
        groups={sortedGroups.map((group) => ({
          id: group.id,
          name: group.name,
        }))}
        afterGroupId={config.after_group_id ?? null}
        onSetDefault={() =>
          onPatch({ [field]: { placement: 'default', after_group_id: null } })
        }
        onSetCustom={() =>
          onPatch({ [field]: { ...config, placement: 'custom' } })
        }
        onAnchorChange={(after_group_id) =>
          onPatch({ [field]: { placement: 'custom', after_group_id } })
        }
      />
    );
  };

  const breakToggle = (item?: LayoutItem) => {
    const owner = item && breakOwners[item.key];

    if (owner === undefined) return null;

    const pageBreak = !!sections[owner]?.page_break_after;

    return (
      <button
        type='button'
        className={`ple-pagebreak ${pageBreak ? 'is-on' : ''}`}
        onClick={() => onPatchSection(owner, { page_break_after: !pageBreak })}
      >
        <span>{pageBreak ? 'Page break' : ''}</span>
      </button>
    );
  };

  const renderItem = (item: LayoutItem) => {
    const width = widthOf(item, sections);

    return (
      <div
        key={item.key}
        id={`ple-item-${item.key}`}
        style={{ flex: `0 0 ${width}`, maxWidth: width }}
      >
        {!sheetStarts.has(item.key) && breakToggle(item)}

        {item.type === 'section' && (
          <SectionBlock
            group={groupMap[item.groupId]}
            section={sections[item.groupId] ?? defaultSection(item.groupId)}
          />
        )}

        {item.type === 'cert' && placementBlock('cert')}
        {item.type === 'findings' && placementBlock('findings')}
      </div>
    );
  };

  const pageNumber = (index: number) => {
    const first =
      1 + pageCounts.slice(0, index).reduce((sum, count) => sum + count, 0);

    return (
      <div
        className={`ple-pagenum ple-pagenum--${layout.page_number_position}`}
      >
        Page {first} of {totalPages}
      </div>
    );
  };

  const renderBand = (variant: 'header' | 'footer', index: number) => {
    const config = variant === 'header' ? header : footer;

    if (!config?.enabled || (index > 0 && !repeats(config))) return null;

    return (
      <HeaderFooterBlock
        variant={variant}
        config={config}
        logoGuid={logoGuid}
      />
    );
  };

  return (
    <div className='ple-sheets' ref={sheetsRef} aria-label={templateName}>
      {sheets.map((sheetItems, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <div className='ple-sheets__break' style={{ width: sheetWidth }}>
              {breakToggle(sheetItems[0])}
            </div>
          )}

          <div
            data-ple-sheet={index}
            className={`ple-doc ${layout.show_borders ? 'is-bordered' : ''}`}
            style={{
              width: sheetWidth,
              minHeight: sheetHeight,
              padding: `${padTop}px ${padRight}px ${padBottom}px ${padLeft}px`,
            }}
          >
            {layout.page_number_position === 'top' && pageNumber(index)}
            {renderBand('header', index)}

            <div className='ple-doc__body'>{sheetItems.map(renderItem)}</div>

            {renderBand('footer', index)}
            {layout.page_number_position === 'bottom' && pageNumber(index)}

            {/* where the paper runs out: a line every page box down the sheet */}
            <div
              className='ple-doc__pagelines'
              style={{ backgroundSize: `100% ${sheetHeight}px` }}
            />
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

export default DocumentPreview;
